import { useEffect, useState } from "react";
import api, { API, formatApiError, tokenStore } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Plus, Trash2, Calendar, Mail, Database, Download, Send, Wrench, AlertTriangle } from "lucide-react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [newDate, setNewDate] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [newReason, setNewReason] = useState("");
  const [backupEmailsText, setBackupEmailsText] = useState("");
  const [backupFrequency, setBackupFrequency] = useState("weekly");
  const [backupBusy, setBackupBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [dupsBusy, setDupsBusy] = useState(false);
  const [dupsResult, setDupsResult] = useState(null); // {groups, total_duplicates}

  const analyzeDuplicates = async () => {
    setDupsBusy(true);
    try {
      const { data } = await api.get("/maintenance/shift-duplicates");
      setDupsResult(data);
      if (data.total_duplicates === 0) {
        toast.success("Aucun doublon détecté 🎉");
      } else {
        toast.warning(`${data.total_duplicates} doublon(s) détecté(s) sur ${data.groups.length} paires étudiant/jour`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDupsBusy(false); }
  };

  const purgeDuplicates = async () => {
    if (!dupsResult || dupsResult.total_duplicates === 0) return;
    if (!window.confirm(`Supprimer définitivement ${dupsResult.total_duplicates} shift(s) en doublon ? Le plus récent sera conservé pour chaque paire étudiant/jour.`)) return;
    setDupsBusy(true);
    try {
      const { data } = await api.post("/maintenance/shift-duplicates/purge");
      toast.success(`${data.deleted} doublon(s) supprimé(s)`);
      setDupsResult({ groups: [], total_duplicates: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDupsBusy(false); }
  };

  const load = async () => {
    const [s, h] = await Promise.all([api.get("/settings"), api.get(`/holidays/${year}`)]);
    setSettings(s.data);
    setHolidays(h.data);
    setBackupEmailsText((s.data.backup_emails || []).join(", "));
    setBackupFrequency(s.data.backup_frequency || "weekly");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const save = async () => {
    try {
      const { data } = await api.patch("/settings", {
        open_mon_fri_start: settings.open_mon_fri_start,
        open_mon_fri_end: settings.open_mon_fri_end,
        open_sat_start: settings.open_sat_start,
        open_sat_end: settings.open_sat_end,
        default_pause_minutes: Number(settings.default_pause_minutes) || 30,
        quota_year_hours_default: Number(settings.quota_year_hours_default) || 650,
      });
      setSettings(data);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addHoliday = async () => {
    if (!newDate) return;
    const next = [...(settings.custom_holidays || []), newDate].filter((v, i, a) => a.indexOf(v) === i);
    const nextReasons = { ...(settings.closure_reasons || {}) };
    if (newReason) nextReasons[newDate] = newReason;
    try {
      const { data } = await api.patch("/settings", { custom_holidays: next, closure_reasons: nextReasons });
      setSettings(data);
      setNewDate(""); setNewReason("");
      load();
      toast.success("Fermeture ajoutée");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeCustom = async (iso) => {
    const next = (settings.custom_holidays || []).filter((d) => d !== iso);
    const nextReasons = { ...(settings.closure_reasons || {}) };
    delete nextReasons[iso];
    try {
      const { data } = await api.patch("/settings", { custom_holidays: next, closure_reasons: nextReasons });
      setSettings(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/notifications/test-email", { to: testEmail || undefined });
      if (data.mocked) {
        toast.warning(`Email mocké (BREVO_API_KEY manquante) — destinataire prévu : ${data.to}`);
      } else {
        toast.success(`Email envoyé à ${data.to} ✓`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailBusy(false); }
  };

  const saveBackupSettings = async () => {
    const emails = backupEmailsText
      .split(/[,;\s]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes("@"));
    try {
      const { data } = await api.patch("/settings", {
        backup_emails: emails,
        backup_frequency: backupFrequency,
      });
      setSettings(data);
      setBackupEmailsText((data.backup_emails || []).join(", "));
      toast.success("Paramètres de sauvegarde enregistrés");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const res = await fetch(`${API}/backup`, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Backup error");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `pecan-backup-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Sauvegarde téléchargée");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBackupBusy(false); }
  };

  const sendBackupNow = async () => {
    setSendBusy(true);
    try {
      const { data } = await api.post("/backup/send-now");
      toast.success(`Sauvegarde envoyée à ${(data.recipients || []).length} destinataire(s) ✓`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendBusy(false); }
  };

  const FREQUENCY_LABELS = {
    daily: "Quotidienne (chaque jour à 23h)",
    weekly: "Hebdomadaire (dimanche 23h)",
    monthly: "Mensuelle (dernier jour du mois, 23h)",
  };

  if (!settings) return <div className="text-[#6b7f79]">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="page-settings">
      <div>
        <p className="label-tiny">Configuration</p>
        <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">Paramètres</h1>
        <p className="text-[#6b7f79] mt-1">Horaires d'ouverture, pause par défaut, quota et jours fériés.</p>
      </div>

      <Card className="card-soft border-0">
        <CardContent className="p-6 space-y-5">
          <h2 className="font-display text-xl text-[#1A2B27]">Horaires d'ouverture</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-tiny">Lundi–Vendredi début</Label>
              <Input type="time" value={settings.open_mon_fri_start || ""} onChange={(e) => setSettings({ ...settings, open_mon_fri_start: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="settings-mf-start" />
            </div>
            <div>
              <Label className="label-tiny">Lundi–Vendredi fin</Label>
              <Input type="time" value={settings.open_mon_fri_end || ""} onChange={(e) => setSettings({ ...settings, open_mon_fri_end: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="settings-mf-end" />
            </div>
            <div>
              <Label className="label-tiny">Samedi début</Label>
              <Input type="time" value={settings.open_sat_start || ""} onChange={(e) => setSettings({ ...settings, open_sat_start: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="settings-sat-start" />
            </div>
            <div>
              <Label className="label-tiny">Samedi fin</Label>
              <Input type="time" value={settings.open_sat_end || ""} onChange={(e) => setSettings({ ...settings, open_sat_end: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="settings-sat-end" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-soft border-0">
        <CardContent className="p-6 space-y-5">
          <h2 className="font-display text-xl text-[#1A2B27]">Règles globales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-tiny">Pause par défaut (min)</Label>
              <Input type="number" min={0} value={settings.default_pause_minutes ?? 30} onChange={(e) => setSettings({ ...settings, default_pause_minutes: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="settings-default-pause" />
            </div>
            <div>
              <Label className="label-tiny">Quota étudiant / an (h) — défaut</Label>
              <Input type="number" min={0} value={settings.quota_year_hours_default ?? 650} onChange={(e) => setSettings({ ...settings, quota_year_hours_default: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="settings-default-quota" />
              <p className="text-[10px] text-[#94ADA7] mt-1">Modifiable par étudiant dans la page Équipe.</p>
            </div>
          </div>
          <Button onClick={save} data-testid="settings-save" className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] shadow-md">
            <Save className="w-4 h-4 mr-2" /> Enregistrer
          </Button>
        </CardContent>
      </Card>

      <Card className="card-soft border-0">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-xl text-[#1A2B27] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#2C4C3B]" /> Notifications email (Brevo)
          </h2>
          <p className="text-sm text-[#6b7f79]">
            Les annulations de shift envoient automatiquement un email à toi et au chef d'atelier.
            Teste ici que la configuration Brevo fonctionne bien.
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[240px]">
              <Label className="label-tiny">Destinataire du test (laisse vide pour t'envoyer à toi)</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="ex. moi@perso.be"
                data-testid="test-email-input"
                className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
              />
            </div>
            <Button
              onClick={sendTestEmail}
              disabled={emailBusy}
              data-testid="test-email-send"
              className="rounded-full bg-[#3C6750] hover:bg-[#1F3528] h-11 shadow-md"
            >
              <Mail className="w-4 h-4 mr-2" /> {emailBusy ? "Envoi…" : "Envoyer un email de test"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="card-soft border-0">
        <CardContent className="p-6 space-y-5">
          <h2 className="font-display text-xl text-[#1A2B27] flex items-center gap-2">
            <Database className="w-5 h-5 text-[#2C4C3B]" /> Sauvegarde complète
          </h2>
          <p className="text-sm text-[#6b7f79]">
            Un fichier Excel multi-onglets reprenant toutes les données (utilisateurs, planning, heures, dispos, notifications…).
            Une sauvegarde automatique est envoyée par email selon la fréquence choisie ci-dessous.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-tiny">Intervalle de sauvegarde automatique</Label>
              <Select value={backupFrequency} onValueChange={setBackupFrequency}>
                <SelectTrigger data-testid="backup-frequency" className="mt-1 h-11 rounded-xl border-[#DDE7E4]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">{FREQUENCY_LABELS.daily}</SelectItem>
                  <SelectItem value="weekly">{FREQUENCY_LABELS.weekly}</SelectItem>
                  <SelectItem value="monthly">{FREQUENCY_LABELS.monthly}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="label-tiny">Emails de destination pour la sauvegarde</Label>
              <Textarea
                value={backupEmailsText}
                onChange={(e) => setBackupEmailsText(e.target.value)}
                placeholder="ex. charles@atelierpecan.be, chef@atelierpecan.be"
                data-testid="backup-emails-input"
                className="mt-1 min-h-[72px] rounded-xl border-[#DDE7E4]"
              />
              <p className="text-[10px] text-[#94ADA7] mt-1">
                Sépare les emails par une virgule. Si vide, la sauvegarde est envoyée à tous les comptes Admin actifs.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={saveBackupSettings}
              data-testid="backup-save-settings"
              className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] shadow-md"
            >
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
            <Button
              onClick={downloadBackup}
              disabled={backupBusy}
              variant="outline"
              data-testid="backup-download"
              className="rounded-full border-[#3C6750] text-[#3C6750] hover:bg-[#E4EBE1]"
            >
              <Download className="w-4 h-4 mr-2" /> {backupBusy ? "Téléchargement…" : "Télécharger maintenant"}
            </Button>
            <Button
              onClick={sendBackupNow}
              disabled={sendBusy}
              variant="outline"
              data-testid="backup-send-now"
              className="rounded-full border-[#3C6750] text-[#3C6750] hover:bg-[#E4EBE1]"
            >
              <Send className="w-4 h-4 mr-2" /> {sendBusy ? "Envoi…" : "Envoyer maintenant par email"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="card-soft border-0">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-xl text-[#1A2B27] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#2C4C3B]" /> Jours fériés et fermetures — {year}
            </h2>
            <div className="flex gap-2 items-center">
              <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 h-10 rounded-xl border-[#DDE7E4]" data-testid="holidays-year" />
            </div>
          </div>
          <p className="text-xs text-[#94ADA7]">
            Les jours fériés belges (badges blancs) sont automatiques. Ajoute ici tes propres fermetures
            (vacances scolaires, fermeture annuelle…) — elles apparaîtront comme « Fermé » dans le planning et chez les étudiants.
          </p>
          <div className="flex flex-wrap gap-2">
            {holidays.map((h) => (
              <Badge
                key={h.date}
                variant="outline"
                className={`rounded-full ${h.source === "be" ? "border-[#DDE7E4] text-[#6b7f79]" : "border-[#3C6750] text-[#3C6750] bg-[#E4EBE1]"}`}
                data-testid={`holiday-${h.date}`}
              >
                {h.date} — {h.name}
                {h.source === "custom" && (
                  <button onClick={() => removeCustom(h.date)} className="ml-1.5 hover:text-[#E53935]" data-testid={`holiday-remove-${h.date}`}>
                    <Trash2 className="w-3 h-3 inline" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-end pt-2 border-t border-[#DDE7E4]">
            <div>
              <Label className="label-tiny">Date</Label>
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="holiday-new-date" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="label-tiny">Motif (optionnel)</Label>
              <Input
                placeholder="Ex. vacances d'été, fermeture annuelle"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
                data-testid="holiday-new-reason"
              />
            </div>
            <Button onClick={addHoliday} data-testid="holiday-add-button" className="rounded-full bg-[#3C6750] hover:bg-[#1F3528] h-11">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ============ MAINTENANCE ============ */}
      <Card className="card-soft border-0" data-testid="card-maintenance">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-xl text-[#1A2B27] flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#2C4C3B]" /> Maintenance
          </h2>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-[#F1EDE3] rounded-xl">
              <AlertTriangle className="w-4 h-4 text-[#3C6750] shrink-0 mt-0.5" />
              <div className="text-sm text-[#6b7f79] flex-1">
                <div className="font-semibold text-[#1A2B27]">Doublons de shifts</div>
                <p className="mt-0.5">
                  Détecte les shifts en double (même étudiant, même jour) qui peuvent gonfler les totaux du planning.
                  L'analyse est un dry-run — la purge conserve le plus récent et supprime définitivement les autres.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={analyzeDuplicates}
                disabled={dupsBusy}
                data-testid="maintenance-dups-analyze"
                variant="outline"
                className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#E4EBE1]"
              >
                <Database className="w-4 h-4 mr-1" /> {dupsBusy ? "Analyse…" : "Analyser"}
              </Button>
              {dupsResult && dupsResult.total_duplicates > 0 && (
                <Button
                  onClick={purgeDuplicates}
                  disabled={dupsBusy}
                  data-testid="maintenance-dups-purge"
                  className="rounded-full bg-[#C62828] hover:bg-[#A01F1F] text-white"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Purger {dupsResult.total_duplicates} doublon(s)
                </Button>
              )}
            </div>

            {dupsResult && dupsResult.groups.length > 0 && (
              <div className="mt-3 border border-[#DDE7E4] rounded-xl overflow-hidden" data-testid="maintenance-dups-list">
                <div className="px-3 py-2 bg-[#F1EDE3] text-[10px] uppercase tracking-wider text-[#2C4C3B] font-semibold">
                  Détails ({dupsResult.groups.length} paire{dupsResult.groups.length > 1 ? "s" : ""})
                </div>
                <div className="max-h-72 overflow-auto divide-y divide-[#DDE7E4]">
                  {dupsResult.groups.map((g, i) => (
                    <div key={`${g.student_id}-${g.date}-${i}`} className="p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-[#1A2B27]">{g.student_name}</span>
                        <span className="text-[#94ADA7]">{g.date} — {g.count} shifts</span>
                      </div>
                      <div className="ml-2 text-xs space-y-0.5">
                        <div className="text-[#2E7D32]">
                          ✓ garder : {g.keep.start} → {g.keep.end} <span className="opacity-60">({(g.keep.created_at || "").slice(0, 19).replace("T", " ")})</span>
                        </div>
                        {g.delete.map((d) => (
                          <div key={d.id} className="text-[#C62828] line-through opacity-70">
                            ✗ à supprimer : {d.start} → {d.end} <span className="opacity-60 no-underline">({(d.created_at || "").slice(0, 19).replace("T", " ")})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
